"use client";

import dynamic from "next/dynamic";
import type { ImageProps } from "next/image";
import { useEffect, useState } from "react";

import { resolveImageSrc } from "@ui/lib/image";

import { useTranslations } from "next-intl";

import IconLoader from "@ui/central-icons/icons/IconLoader";
import IconPause from "@ui/central-icons/icons/IconPause";
import IconPlay from "@ui/central-icons/icons/IconPlay";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import clsx from "clsx";

import { useSoundEffectsPlayer } from "./SoundEffectsPlayerContext";

const Orb = dynamic(() => import("../core/Orb/Orb").then(m => m.Orb), {
  ssr: false,
});

interface AudioRowPlayControlProps {
  id: string;
  audioUrl?: string;
  thumbnailImageProps?: ImageProps;
  title: string;
  onPlay?: () => void;
  knownDuration?: number;
}

export function AudioRowPlayControl({
  id,
  audioUrl,
  thumbnailImageProps,
  title,
  onPlay,
  knownDuration,
}: AudioRowPlayControlProps) {
  const t = useTranslations("ClientKeys.Globals");
  const { activeId, playState, analyser, handlePlay } = useSoundEffectsPlayer();

  const isActive = activeId === id;
  const isLoading = isActive && playState === "loading";
  const isPlaying = isActive && playState === "playing";
  const shouldAnimateOrb = isLoading || isPlaying;

  // Delay showing the spinner so fast-loading audio doesn't cause a flicker
  const [showLoader, setShowLoader] = useState(false);
  useEffect(() => {
    if (!isLoading) {
      setShowLoader(false);
      return;
    }
    const timer = setTimeout(() => setShowLoader(true), 150);
    return () => clearTimeout(timer);
  }, [isLoading]);

  return (
    <>
      {/* Invisible overlay button — sits below action buttons in z-order so they intercept first */}
      {audioUrl && (
        <button
          type='button'
          className='focus-visible:tw-outline-focus tw-absolute tw-inset-0 tw-z-0'
          onClick={() => {
            // Only call onPlay when starting (not when toggling off)
            if (activeId !== id) onPlay?.();
            handlePlay({ id, audioUrl, knownDuration });
          }}
          aria-label={isPlaying || isLoading ? t("pause_item", { title }) : t("play_item", { title })}
        />
      )}

      {/* Thumbnail slot — pointer-events-none so clicks fall through to the overlay button */}
      <div
        className={clsx(
          "tw-pointer-events-none tw-relative tw-size-12 tw-shrink-0 tw-overflow-hidden tw-rounded-sm tw-transition-transform tw-duration-200 tw-ease-out",
          isActive && "tw-scale-110",
        )}
      >
        {thumbnailImageProps ? (
          <Orb
            texture={{
              texture: resolveImageSrc(thumbnailImageProps.src),
              image: resolveImageSrc(thumbnailImageProps.src),
              placeholder: thumbnailImageProps.blurDataURL,
            }}
            animated={shouldAnimateOrb}
            outputAnalyser={analyser}
            cornerRadius={0}
            image={{ sizes: "48px" }}
            fadeInDuration={1}
          />
        ) : (
          <div className='tw-size-full tw-bg-white/20' />
        )}
        <div aria-hidden='true' className='tw-absolute tw-inset-0 tw-bg-black/10' />

        {/* Spinner while loading */}
        {showLoader && audioUrl && (
          <div
            aria-hidden='true'
            className='tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'
          >
            <IconLoader className={clsx(iconStrokeFix, "tw-size-7", "tw-animate-spin tw-text-white tw-drop-shadow")} />
          </div>
        )}

        {/* Play icon: show when idle, and also during the loading grace period so there's no blank gap */}
        {!isPlaying && !showLoader && audioUrl && (
          <div
            aria-hidden='true'
            data-testid='play-icon-overlay'
            className='tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'
          >
            <IconPlay
              className={clsx(iconStrokeFix, "tw-size-7", "tw-drop-shadow tw-text-white")}
              fill='currentColor'
              stroke='none'
            />
          </div>
        )}

        {/* Pause icon when playing */}
        {isPlaying && (
          <div className='tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'>
            <IconPause
              className={clsx(iconStrokeFix, "tw-size-7", "tw-drop-shadow tw-text-white")}
              fill='currentColor'
              stroke='none'
            />
          </div>
        )}
      </div>
    </>
  );
}
